import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { AnimatePresence, m } from "framer-motion";
import { Input } from "./Form";
import * as Yup from "yup";
import MessageBox from "./MessageBox";
import { resetForm, sendEmail } from "../Functions/Utilities";
import Buttons from "./Button/Buttons";
import { Link, useNavigate } from "react-router-dom";
import { useConversion } from "../contexts/ConversionContext";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../hooks/useLanguage";

// Form validation schema function that accepts translation function
const createContactFormSchema = (t) => Yup.object().shape({
  name: Yup.string().required(t('common:contactForm.fieldRequired')),
  company: Yup.string(),
  email: Yup.string()
    .email(t('common:contactForm.invalidEmail'))
    .required(t('common:contactForm.fieldRequired')),
  phone: Yup.string(),
  interest: Yup.string().required(t('common:contactForm.selectOption')),
  budget: Yup.number().min(0).max(30000),
  message: Yup.string().max(500, t('common:contactForm.maxCharactersExceeded')),
});

const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

const ModalPopupContact = ({ isOpen, onClose }) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const { setConversion } = useConversion();
  const { t } = useTranslation(['common']);
  const { getLocalizedPath } = useLanguage();

  useEffect(() => {
    // Close the popup after 5 seconds if form is successfully submitted
    let timer;
    if (isSuccess) {
      timer = setTimeout(() => {
        onClose();
        // Reset success state when closing
        setIsSuccess(false);
      }, 10000);
    }

    // Cleanup timer on component unmount
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isSuccess, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center'
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
          isSuccess ? "w-[440px]" : "w-[640px]"
        } max-w-[94vw] bg-white rounded-sm shadow-xl`}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className='absolute top-4 right-4 z-10 text-gray-500 hover:text-gray-700 text-[24px] font-bold'
          aria-label='Close modal'
        >
          ×
        </button>

        {/* Modal content */}
        <div className='max-h-[94vh] overflow-y-auto'>
          {isSuccess ? (
            <div className='flex flex-col items-center justify-center min-h-[150px] py-8 px-2 w-full'>
              <div className='flex justify-center mb-0'>
                <img
                  src='/assets/img/iq_tick.gif'
                  alt='Success'
                  className='w-12 h-12 mb-0'
                />
              </div>
              <h2
                className='text-center font-[Oxanium] text-[22px] text-inaqpurple font-medium mb-0
              '
              >
                {t('common:contactForm.thankYouTitle')}
              </h2>
              <p className='font-rubik text-[14px] sm:text-[13px] leading-[20px] text-inaqgold inline-block mb-12 sm:mb-8 sm:mx-4 mx-8 text-center'>
                {t('common:contactForm.thankYouMessage')}
              </p>
              <div className='flex space-x-4 mt-2'>
                <Link
                  to='/blog'
                  className='border-[1px] border-inaqgold border-solid btn-md font-medium font-rubik bg-transparent text-inaqgold hover:text-inaqgold hover:text-opacity-80 py-2 px-4 rounded-sm btn btn-secondary'
                  style={{ "--text-color": "#fff" }}
                >
                  {t('common:buttons.goToBlog')}
                </Link>
                <button
                  type='button'
                  onClick={onClose}
                  className='border-[1px] border-inaqgold border-solid btn-md font-normal font-rubik bg-inaqgold text-white py-2 px-4 rounded-sm btn btn-secondary'
                  style={{ "--text-color": "#fff" }}
                >
                  {t('common:buttons.close')}
                </button>
              </div>
            </div>
          ) : (
            <div className='px-14 pt-4 pb-8 sm:p-8'>
              {/* Header */}
              <header className='flex flex-col w-full items-start gap-0 mb-4'>
                <h1 className='heading-5 font-["Oxanium"] text-[36px] sm:text-[24px] text-inaqpurple font-[500] tracking-[-2px] lg:leading-[46px] mb-2 mt-4'>
                  {t('common:contactForm.title')}
                </h1>
                <p className='font-rubik text-[14px] sm:text-[13px] leading-[20px] text-inaqgold inline-block mb-[15px] sm:mb-[10px] sm:mx-8 mx-0'>
                  {t('common:contactForm.subtitle')}
                </p>
              </header>

              <Formik
                initialValues={{
                  name: "",
                  company: "",
                  email: "",
                  phone: "",
                  interest: "strategy",
                  budget: 8000, // Start with the middle value
                  message: "",
                }}
                validationSchema={createContactFormSchema(t)}
                onSubmit={async (values, actions) => {
                  actions.setSubmitting(true);

                  // Use the sendEmail utility function from Utilities.js
                  const response = await sendEmail(values);

                  if (response.status === "success") {
                    // Create conversion data
                    const conversionInfo = setConversion({
                      formType: "contact",
                      formLocation: "modal",
                      interest: values.interest,
                      budget: values.budget,
                    });
                    console.log("Created conversion info:", conversionInfo);

                    // Push form submission event to GTM dataLayer
                    if (window.dataLayer) {
                      window.dataLayer.push({
                        event: "formSubmissionComplete",
                        conversionType: "contact",
                        formLocation: "modal",
                        formInterest: values.interest,
                        formBudget: values.budget,
                      });
                    }

                    // Navigate first, then close modal
                    const dakujemePath = getLocalizedPath("/dakujeme");
                    console.log("Navigating to", dakujemePath, "with state:", conversionInfo);
                    navigate(dakujemePath, {
                      state: { conversion: conversionInfo },
                    });

                    // Close modal after navigation
                    setTimeout(() => {
                      console.log("Closing modal");
                      onClose();
                    }, 100);
                  } else {
                    // Handle error
                    actions.setSubmitting(false);
                    actions.setStatus("error");
                    console.error("Form submission failed:", response.message);
                  }
                }}
              >
                {({ isSubmitting, status, values, setFieldValue }) => (
                  <Form className='w-full font-rubik'>
                    <div className='grid grid-cols-2 gap-x-12 gap-y-[18px]'>
                      {/* Name field */}
                      <div>
                        <Input
                          showErrorMsg={false}
                          name='name'
                          type='text'
                          className='py-[8px] px-[12px] w-full border-[1px] border-solid border-[#d7d7d7] rounded-md bg-pink-50'
                          placeholder=''
                          labelClass='mb-[5px]'
                          label={
                            <span className='text-sm font-normal text-gray-700 leading-5 font-rubik'>
                              {t('common:contactForm.yourName')}
                            </span>
                          }
                        />
                      </div>

                      {/* Company field */}
                      <div>
                        <Input
                          showErrorMsg={false}
                          name='company'
                          type='text'
                          className='py-[8px] px-[12px] w-full border-[1px] border-solid border-[#d7d7d7] rounded-md bg-pink-50'
                          placeholder=''
                          labelClass='mb-[5px]'
                          label={
                            <span className='text-sm font-normal text-gray-700 leading-5 font-rubik'>
                              {t('common:contactForm.company')}
                            </span>
                          }
                        />
                      </div>

                      {/* Email field - full width */}
                      <div className='col-span-2'>
                        <Input
                          showErrorMsg={false}
                          name='email'
                          type='email'
                          className='py-[8px] px-[12px] w-full border-[1px] border-solid border-[#d7d7d7] rounded-md bg-pink-50'
                          placeholder=''
                          labelClass='mb-[5px]'
                          label={
                            <span className='text-sm font-normal text-gray-700 leading-5 font-rubik'>
                              {t('common:contactForm.email')}
                            </span>
                          }
                        />
                      </div>

                      {/* Phone field - full width with "optional" text */}
                      <div className='col-span-2 relative'>
                        <div className='flex justify-between mb-[5px]'>
                          <span className='text-sm font-normal text-gray-700 leading-5 font-rubik'>
                            {t('common:contactForm.phone')}
                          </span>
                          <span className='text-sm font-normal text-gray-500 leading-5 font-rubik'>
                            {t('common:contactForm.optional')}
                          </span>
                        </div>
                        <Input
                          showErrorMsg={false}
                          name='phone'
                          type='tel'
                          className='py-[8px] px-[12px] w-full border-[1px] border-solid border-[#d7d7d7] rounded-md bg-pink-50'
                          placeholder=''
                        />
                      </div>

                      {/* Interest radio buttons */}
                      <div className='col-span-2 mt-2'>
                        <div className='text-sm font-normal text-gray-700 leading-5 font-rubik mb-4'>
                          {t('common:contactForm.interestedIn')}
                        </div>
                        <div className='flex flex-col gap-4 mb-4'>
                          {/* Strategy option */}
                          <div className='radio-wrapper'>
                            <Field
                              name='interest'
                              value='strategy'
                              component={({ field }) => (
                                <label className='flex items-start gap-3 cursor-pointer'>
                                  <div className='relative mt-0.5'>
                                    <input
                                      type='radio'
                                      id='interest-strategy'
                                      {...field}
                                      value='strategy'
                                      checked={field.value === "strategy"}
                                      className='absolute opacity-0 w-0 h-0 -z-10 pointer-events-none hidden'
                                    />
                                    <span
                                      className={`flex items-center justify-center w-5 h-5 rounded-full border ${
                                        field.value === "strategy"
                                          ? "border-inaqgold"
                                          : "!border-[#d7d7d7]"
                                      }`}
                                    >
                                      {field.value === "strategy" && (
                                        <span className='w-3 h-3 bg-inaqgold rounded-full'></span>
                                      )}
                                    </span>
                                  </div>
                                  <span className='text-sm font-normal text-gray-700 leading-5 font-rubik'>
                                    {t('common:contactForm.strategyAnalysis')}
                                  </span>
                                </label>
                              )}
                            />
                          </div>

                          {/* Service option */}
                          <div className='radio-wrapper'>
                            <Field
                              name='interest'
                              value='service'
                              component={({ field }) => (
                                <label className='flex items-start gap-3 cursor-pointer'>
                                  <div className='relative mt-0.5'>
                                    <input
                                      type='radio'
                                      id='interest-service'
                                      {...field}
                                      value='service'
                                      checked={field.value === "service"}
                                      className='absolute opacity-0 w-0 h-0 -z-10 pointer-events-none hidden'
                                    />
                                    <span
                                      className={`flex items-center justify-center w-5 h-5 rounded-full border ${
                                        field.value === "service"
                                          ? "border-inaqgold"
                                          : "!border-[#d7d7d7]"
                                      }`}
                                    >
                                      {field.value === "service" && (
                                        <span className='w-3 h-3 bg-inaqgold rounded-full'></span>
                                      )}
                                    </span>
                                  </div>
                                  <span className='text-sm font-normal text-gray-700 leading-5 font-rubik'>
                                    {t('common:contactForm.specificService')}
                                  </span>
                                </label>
                              )}
                            />
                          </div>

                          {/* Undecided option */}
                          <div className='radio-wrapper'>
                            <Field
                              name='interest'
                              value='undecided'
                              component={({ field }) => (
                                <label className='flex items-start gap-3 cursor-pointer'>
                                  <div className='relative mt-0.5'>
                                    <input
                                      type='radio'
                                      id='interest-undecided'
                                      {...field}
                                      value='undecided'
                                      checked={field.value === "undecided"}
                                      className='absolute opacity-0 w-0 h-0 -z-10 pointer-events-none hidden'
                                    />
                                    <span
                                      className={`flex items-center justify-center w-5 h-5 rounded-full border ${
                                        field.value === "undecided"
                                          ? "border-inaqgold"
                                          : "!border-[#d7d7d7]"
                                      }`}
                                    >
                                      {field.value === "undecided" && (
                                        <span className='w-3 h-3 bg-inaqgold rounded-full'></span>
                                      )}
                                    </span>
                                  </div>
                                  <span className='text-sm font-normal text-gray-700 leading-5 font-rubik'>
                                    {t('common:contactForm.notSureYet')}
                                  </span>
                                </label>
                              )}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Budget slider */}
                      <div className='col-span-2 mt-0'>
                        <div className='flex justify-between mb-2'>
                          <p className='text-sm font-normal text-gray-700 leading-5 font-rubik'>
                            {t('common:contactForm.monthlyBudget')}
                          </p>
                          <div className='text-sm font-normal text-right text-gray-700 leading-5 font-rubik'>
                            €{" "}
                            {values.budget === 30000
                              ? "30 000+"
                              : formatNumber(values.budget)}
                          </div>
                        </div>

                        {/* Modern Range Slider */}
                        <div className='relative pt-1'>
                          <div className='relative'>
                            {/* Slider Track (Background) */}
                            <div className='w-full h-2 bg-[#e8e9f2] rounded-full'></div>

                            {/* Slider Progress (Filled area) */}
                            <div
                              className='absolute top-0 h-2 bg-inaqgold rounded-full'
                              style={{
                                width: `${(values.budget / 30000) * 100}%`,
                              }}
                            ></div>

                            {/* Slider Thumb (Handle) */}
                            <div
                              className='absolute top-0 w-4 h-4 bg-inaqgold rounded-full shadow-md transform -translate-y-1 -translate-x-2 cursor-pointer'
                              style={{
                                left: `${(values.budget / 30000) * 100}%`,
                              }}
                            ></div>

                            {/* Invisible Range Input for functionality */}
                            <input
                              type='range'
                              min='0'
                              max='30000'
                              step='2000'
                              value={values.budget}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                setFieldValue("budget", val);
                              }}
                              className='absolute top-0 w-full h-2 opacity-0 cursor-pointer z-10'
                            />
                          </div>
                        </div>
                      </div>

                      {/* Additional comment textarea */}
                      <div className='col-span-2 mt-6'>
                        <div className='flex justify-between mb-1'>
                          <span className='text-sm font-normal text-gray-700 leading-5 font-rubik'>
                            {t('common:contactForm.anythingElse')}
                          </span>
                          <span className='text-sm font-normal text-gray-500 leading-5 font-rubik'>
                            {t('common:contactForm.maxCharacters')}
                          </span>
                        </div>
                        <Field
                          as='textarea'
                          name='message'
                          rows='3'
                          className='py-[8px] px-[12px] w-full border-[1px] border-solid border-[#d7d7d7] rounded-md bg-pink-50 resize-none'
                        />
                      </div>

                      {/* Submit button */}
                      <div className='col-span-2 flex justify-end mt-2'>
                        <Buttons
                          type='submit'
                          className='font-normal font-rubik bg-inaqgold  text-white py-2 px-4 rounded-sm border-inaqgold'
                          size='md'
                          color='#fff'
                          title={t('common:buttons.send')}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    {/* Success/Error message */}
                    <AnimatePresence>
                      {status === "error" && (
                        <m.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <MessageBox
                            className='mt-6 py-[10px]'
                            theme='message-box01'
                            variant='danger'
                            message={t('common:contactForm.submitError')}
                          />
                        </m.div>
                      )}
                    </AnimatePresence>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalPopupContact;
